import Axios from 'axios';
import i18next from 'i18next';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { errorCatch } from './errorCatcher.reducer';
import { RootState } from '../app/rootReducer';

export type Config = {
  countryIso2Code: string;
  isEnhancedBilling?: boolean;
  registrationNumberTypes: RegistrationNumberType[];
};

export type RegistrationNumberType = {
  id: string;
  typeId: number;
  code: string;
  displayName: string;
  displayNameTranslations: Record<string, string>;
  placeholder: string;
  isMandatory: boolean;
  maxCount: number;
  validationRules: RegistrationNumberValidationRule | null;
  displayOrder: number;
  isEditable?: boolean;
};
type RegistrationNumberValidationRule = {
  matchMode: 'all' | 'any';
  rules: ValidationRule[];
};
type ValidationRule = {
  type: string;
  message: Record<string, string>;
  pattern?: string;
  algorithm?: string;
};

export const getDefaultConfig = (isUS?: boolean) =>
  ({
    countryIso2Code: 'US',
    registrationNumberTypes: [
      {
        id: '1',
        typeId: 4,
        code: 'FED_TAX_ID',
        displayName: i18next.t(
          'billing:screens.signUpScreen.companyInformationSection.businessIdLabel',
          'Company Federal Tax ID / Business Identification Number'
        ),
        displayNameTranslations: {
          en: i18next.t(
            'billing:screens.signUpScreen.companyInformationSection.businessIdLabel',
            'Company Federal Tax ID / Business Identification Number'
          )
        },
        placeholder: '',
        isMandatory: isUS,
        maxCount: 1,
        validationRules: isUS
          ? {
              matchMode: 'all',
              rules: [
                {
                  type: 'regex',
                  pattern: '^(\\d{3}-\\d{2}-\\d{4}|\\d{2}-\\d{7})$',
                  message: {
                    en: i18next.t(
                      'billing:screens.signUpScreen.errorMessages.businessId.USTaxId',
                      'Company Federal Tax ID / Business Identification Number should have XXX-XX-XXXX or XX-XXXXXXX format.'
                    )
                  }
                }
              ]
            }
          : null,
        displayOrder: 1,
        isEditable: true
      }
    ]
  } as Config);
export const defaultConfig = getDefaultConfig(true);

export const getEnhancedBillingEnabledCountries = createAsyncThunk(
  'countryConfig/getEnhancedBillingEnabledCountries',
  async (_, { dispatch }): Promise<string[]> => {
    try {
      const { data } = await Axios.get<{ enabledCountries: string[] }>(
        '/api/v3.3/countryFeatures/EnhancedBilling/countries'
      );
      return data.enabledCountries;
    } catch (error) {
      dispatch(errorCatch({ place: 'countryConfig/getEnhancedBillingEnabledCountries.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

type GetCountryRegNumberConfigParams = {
  countryIso2: string;
};
export const getCountryRegNumberConfig = createAsyncThunk(
  'countryConfig/getCountryRegNumberConfig',
  async ({ countryIso2 }: GetCountryRegNumberConfigParams, { dispatch, getState }): Promise<Config> => {
    try {
      const { enhancedBillingEnabledCountries } = (getState() as RootState).countryConfig;

      if (!enhancedBillingEnabledCountries.includes(countryIso2)) {
        return getDefaultConfig(countryIso2 === 'US');
      }

      const { data } = await Axios.get<Config>(`/api/v3.3/countries/${countryIso2}/registration-number-configs`);
      return { ...data, isEnhancedBilling: true };
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return getDefaultConfig(countryIso2 === 'US');
      }
      dispatch(errorCatch({ place: 'countryConfig/getCountryRegNumberConfig.rejected', data: error }));
      return Promise.reject(error);
    }
  }
);

export type InitState = {
  enhancedBillingEnabledCountries: string[];
  config: Config;
  isFetching: boolean;
};

export const initialState = {
  enhancedBillingEnabledCountries: [],
  config: getDefaultConfig(true),
  isFetching: false
} as InitState;

const slice = createSlice({
  name: 'countryConfig',
  initialState,
  reducers: {},
  extraReducers: {
    [getCountryRegNumberConfig.pending.toString()]: state => {
      state.isFetching = true;
    },
    [getCountryRegNumberConfig.fulfilled.toString()]: (state, action) => {
      state.config = action.payload;
      state.isFetching = true;
    },
    [getCountryRegNumberConfig.rejected.toString()]: state => {
      state.isFetching = false;
    },
    [getEnhancedBillingEnabledCountries.pending.toString()]: state => {
      state.isFetching = true;
    },
    [getEnhancedBillingEnabledCountries.fulfilled.toString()]: (state, action) => {
      state.enhancedBillingEnabledCountries = action.payload;
      state.isFetching = false;
    },
    [getEnhancedBillingEnabledCountries.rejected.toString()]: state => {
      state.isFetching = false;
    }
  }
});

export default slice.reducer;
